import React, { useEffect, useState, useRef } from 'react';
import QuoteGenerator from './QuoteGenerator';
import HistoryModal from './HistoryModal';

interface Quote {
    id: number;
    company_name: string;
    contact_name: string;
    phone: string;
    email: string;
    sku: string;
    daily_volume: string;
    modules: string[];
    message: string;
    referral_sources: string[];
    referral_source_etc: string;
    created_at: string;
    access_code: string;
    status: string;
    sales_rep_id: number | null;
    internal_remarks: string;
    status_updated_at: string;
    sales_rep_name?: string;
}

interface User {
    id: number;
    username: string;
    role: string;
}



const QuoteList: React.FC = () => {
    const [quotes, setQuotes] = useState<Quote[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState<{ id: number, username: string, role: string } | null>(null);

    // Panel & Selection State
    const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [showGenerator, setShowGenerator] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    // Panel Form State
    const [panelRemarks, setPanelRemarks] = useState('');

    // Manual Modal State
    const [showManualModal, setShowManualModal] = useState(false);
    const [manualForm, setManualForm] = useState({
        companyName: '', contactName: '', phone: '', email: '', sku: '', dailyVolume: '',
        modules: [] as string[], message: '', referralSources: [] as string[], referralSourceEtc: '',
        status: '견적요청', salesRepId: '', internalRemarks: ''
    });

    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        // Decode token for role
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join(''));
                setCurrentUser(JSON.parse(jsonPayload));
            } catch (e) {
                console.error('Error decoding token', e);
            }
        }

        fetchQuotes();
        fetchUsers();

        // 실시간 갱신: socket.io 대신 폴링(15초). CF Pages는 Durable Objects 없이 $0 유지.
        const pollId = setInterval(() => {
            fetchQuotes();
        }, 15000);

        return () => {
            clearInterval(pollId);
        };
    }, []); // Only run once on mount

    const fetchQuotes = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/quotes', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setQuotes(data);
                if (selectedQuote) {
                    const updated = data.find((q: Quote) => q.id === selectedQuote.id);
                    if (updated) {
                        setSelectedQuote(updated);
                    }
                }
            }
        } catch (error) {
            console.error('Failed to fetch quotes');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/admin/users', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setUsers(data);
            }
        } catch (error) {
            console.error('Failed to fetch users');
        }
    };

    const handleRowClick = (quote: Quote) => {
        setSelectedQuote(quote);
        setIsPanelOpen(true);
        setPanelRemarks('');
    };

    const closePanel = () => {
        setIsPanelOpen(false);
        setSelectedQuote(null);
    };

    const getNextStatus = (currentStatus: string) => {
        if (currentStatus === '견적요청') return '1차상담 완료';
        if (currentStatus === '1차상담 완료') return '1차견적서 완료';
        return null;
    };

    const handleStatusChange = async (newStatus: string) => {
        if (!selectedQuote) return;

        if (!window.confirm(`'${newStatus}' 상태로 변경하시겠습니까?`)) return;

        const now = new Date();
        const timestamp = now.getFullYear() + '-' +
            String(now.getMonth() + 1).padStart(2, '0') + '-' +
            String(now.getDate()).padStart(2, '0') + ' ' +
            String(now.getHours()).padStart(2, '0') + ':' +
            String(now.getMinutes()).padStart(2, '0') + ':' +
            String(now.getSeconds()).padStart(2, '0');

        const statusRemark = `\n[System] 상태 변경: ${selectedQuote.status} -> ${newStatus} (${timestamp}, by ${currentUser?.username})`;
        const updatedRemarks = (selectedQuote.internal_remarks || '') + statusRemark;

        await updateQuote(selectedQuote.id, {
            status: newStatus,
            internalRemarks: updatedRemarks
        });
    };

    const handleSaveRemarks = async () => {
        if (!selectedQuote || !panelRemarks.trim()) return;
        const now = new Date();
        const timestamp = now.getFullYear() + '-' +
            String(now.getMonth() + 1).padStart(2, '0') + '-' +
            String(now.getDate()).padStart(2, '0') + ' ' +
            String(now.getHours()).padStart(2, '0') + ':' +
            String(now.getMinutes()).padStart(2, '0') + ':' +
            String(now.getSeconds()).padStart(2, '0');

        const newEntry = `\n[Memo] ${panelRemarks} (${timestamp}, ${currentUser?.username})`;
        const updatedRemarks = (selectedQuote.internal_remarks || '') + newEntry;
        await updateQuote(selectedQuote.id, { internalRemarks: updatedRemarks });
        setPanelRemarks('');
    };

    const handleAssignRep = async (repId: string) => {
        if (!selectedQuote) return;
        await updateQuote(selectedQuote.id, { salesRepId: repId ? parseInt(repId) : null });
    };

    const updateQuote = async (id: number, data: any) => {
        try {
            const token = localStorage.getItem('token');
            const quote = quotes.find(q => q.id === id);
            if (!quote) return;

            const payload = {
                status: data.status !== undefined ? data.status : quote.status,
                salesRepId: data.salesRepId !== undefined ? data.salesRepId : quote.sales_rep_id,
                internalRemarks: data.internalRemarks !== undefined ? data.internalRemarks : quote.internal_remarks
            };

            const response = await fetch(`/api/admin/quotes/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                fetchQuotes();
            } else {
                const err = await response.json();
                alert('업데이트 실패: ' + (err.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error updating quote:', error);
            alert('오류가 발생했습니다.');
        }
    };

    const handleDeleteQuote = async () => {
        if (!selectedQuote || !isAdmin) return;
        if (!window.confirm('정말로 이 견적을 삭제하시겠습니까? 복구할 수 없습니다.')) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/admin/quotes/${selectedQuote.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                alert('삭제되었습니다.');
                closePanel();
                fetchQuotes();
            } else {
                alert('삭제 실패');
            }
        } catch (error) {
            console.error('Error deleting quote:', error);
            alert('오류 발생');
        }
    };

    const checkDuplicate = async (companyName: string) => {
        if (!companyName.trim()) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/admin/quotes/check-duplicate?companyName=${encodeURIComponent(companyName)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.exists) {
                alert(result.message);
                // Optional: Clear input or set error state
                setManualForm(prev => ({ ...prev, companyName: '' })); // Enforce "Input impossible"
            }
        } catch (error) {
            console.error('Check duplicate failed', error);
        }
    };

    // --- Manual Entry Logic ---
    const handleManualSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/admin/quotes/manual', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(manualForm)
            });
            if (response.ok) {
                alert('수동 입력 성공');
                setShowManualModal(false);
                setManualForm({
                    companyName: '', contactName: '', phone: '', email: '', sku: '', dailyVolume: '',
                    modules: [], message: '', referralSources: [], referralSourceEtc: '', status: '견적요청', salesRepId: '', internalRemarks: ''
                });
                fetchQuotes();
            } else {
                const err = await response.json();
                alert(err.error || '수동 입력 실패');
            }
        } catch (error) { console.error(error); alert('오류 발생'); }
    };

    // --- Excel Upload Logic ---
    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]; if (!file) return;
        const formData = new FormData(); formData.append('file', file);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/admin/quotes/upload', {
                method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
            });
            const result = await response.json();
            if (response.ok) { alert(`업로드 완료: ${result.count}건 성공`); fetchQuotes(); }
            else { alert('업로드 실패: ' + result.error); }
        } catch (e) { alert('오류'); }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const downloadSampleExcel = () => {
        // Create a simple CSV with BOM for Korean Excel compatibility
        const headers = ["업체명", "담당자", "연락처", "이메일", "SKU", "일출고량", "메모", "유입경로"];
        const sampleRow = ["(예시)천일물류", "김담당", "010-1234-5678", "test@example.com", "5", "100", "견적부탁드립니다", "네이버무료검색"];
        const csvContent = "\uFEFF" + headers.join(",") + "\n" + sampleRow.join(",");

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "견적업로드_양식.csv");
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) return <div>로딩 중...</div>;

    const isAdmin = currentUser?.role === 'admin';
    const nextStatus = selectedQuote ? getNextStatus(selectedQuote.status) : null;
    const statusOptions = ['견적요청', '1차상담 완료', '1차견적서 완료'];

    return (
        <div className="admin-section" style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center' }}>
                <span style={{ marginRight: 'auto', fontWeight: 'bold' }}>
                    {currentUser ? `${currentUser.username} (${isAdmin ? '관리자' : '영업담당'})` : ''} - Total: {quotes.length}
                </span>

                <button onClick={() => setShowManualModal(true)} style={{ padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>+ 수동 입력</button>
                {isAdmin && (
                    <>
                        <button onClick={downloadSampleExcel} style={{ padding: '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>양식 다운로드</button>
                        <button onClick={() => fileInputRef.current?.click()} style={{ padding: '8px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>엑셀 업로드</button>
                    </>
                )}

                <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".xlsx, .xls, .csv" onChange={handleFileUpload} />
            </div>

            {/* Read-Only Table */}
            <div style={{ overflowX: 'auto', transition: 'width 0.3s' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1500px', fontSize: '13px', tableLayout: 'fixed' }}>
                    <colgroup>
                        <col style={{ width: '50px' }} /> {/* No */}
                        <col style={{ width: '90px' }} /> {/* Status */}
                        <col style={{ width: '80px' }} /> {/* Rep */}
                        <col style={{ width: '120px' }} /> {/* Company */}
                        <col style={{ width: '80px' }} /> {/* Contact */}
                        <col style={{ width: '110px' }} /> {/* Phone */}
                        <col style={{ width: '150px' }} /> {/* Email */}
                        <col style={{ width: '100px' }} /> {/* SKU/Volume */}
                        <col style={{ width: '150px' }} /> {/* Modules */}
                        <col style={{ width: '100px' }} /> {/* Access Code */}
                        <col style={{ width: '120px' }} /> {/* Referral */}
                        <col /> {/* Memo (Rest) */}
                        <col style={{ width: '150px' }} /> {/* Date */}
                    </colgroup>
                    <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                            <th style={{ padding: '10px' }}>No.</th>
                            <th style={{ padding: '10px' }}>상태</th>
                            <th style={{ padding: '10px' }}>담당자</th>
                            <th style={{ padding: '10px' }}>업체명</th>
                            <th style={{ padding: '10px' }}>담당자(고객)</th>
                            <th style={{ padding: '10px' }}>연락처</th>
                            <th style={{ padding: '10px' }}>이메일</th>
                            <th style={{ padding: '10px' }}>SKU / 물량</th>
                            <th style={{ padding: '10px' }}>필요 모듈</th>
                            <th style={{ padding: '10px' }}>발급 코드</th>
                            <th style={{ padding: '10px' }}>유입 경로</th>
                            <th style={{ padding: '10px' }}>메모</th>
                            <th style={{ padding: '10px' }}>접수일시</th>
                        </tr>
                    </thead>
                    <tbody>
                        {quotes.map((quote) => (
                            <tr
                                key={quote.id}
                                onClick={() => handleRowClick(quote)}
                                style={{
                                    borderBottom: '1px solid #dee2e6',
                                    cursor: 'pointer',
                                    backgroundColor: selectedQuote?.id === quote.id ? '#e8f0fe' : 'transparent'
                                }}
                                className="quote-row-hover"
                            >
                                <td style={{ padding: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{quote.id}</td>
                                <td style={{ padding: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    <span style={{
                                        padding: '4px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold',
                                        backgroundColor: quote.status === '견적요청' ? '#ffeeba' : quote.status === '1차상담 완료' ? '#c3e6cb' : '#b8daff',
                                        color: '#333'
                                    }}>
                                        {quote.status}
                                    </span>
                                </td>
                                <td style={{ padding: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{quote.sales_rep_name || '-'}</td>
                                <td style={{ padding: '10px', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{quote.company_name}</td>
                                <td style={{ padding: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{quote.contact_name}</td>
                                <td style={{ padding: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{quote.phone}</td>
                                <td style={{ padding: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{quote.email}</td>
                                <td style={{ padding: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{quote.sku} / {quote.daily_volume}</td>
                                <td style={{ padding: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {quote.modules && quote.modules.length > 0 && <span style={{ padding: '2px 4px', backgroundColor: '#e9ecef', borderRadius: '4px', fontSize: '10px' }}>{quote.modules[0]}...</span>}
                                </td>
                                <td style={{ padding: '10px', color: '#0056b3', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{quote.access_code}</td>
                                <td style={{ padding: '10px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{quote.referral_sources?.[0] || '-'}</td>
                                <td style={{ padding: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#666' }}>{quote.message}</td>
                                <td style={{ padding: '10px', fontSize: '12px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {new Date(quote.created_at).toLocaleString('ko-KR', {
                                        year: 'numeric', month: '2-digit', day: '2-digit',
                                        hour: '2-digit', minute: '2-digit', second: '2-digit',
                                        hour12: false
                                    })}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Slide-out Panel */}
            <div style={{
                position: 'fixed', top: 0, right: isPanelOpen ? 0 : '-500px', width: '450px', height: '100%',
                backgroundColor: 'white', boxShadow: '-2px 0 10px rgba(0,0,0,0.1)',
                transition: 'right 0.3s ease-in-out', zIndex: 1000,
                display: 'flex', flexDirection: 'column'
            }}>
                {selectedQuote && (
                    <>
                        <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8f9fa' }}>
                            <h3 style={{ margin: 0 }}>견적 상세 정보 #{selectedQuote.id}</h3>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                {isAdmin && (
                                    <button onClick={handleDeleteQuote} style={{ border: 'none', background: '#dc3545', color: 'white', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>삭제</button>
                                )}
                                <button onClick={closePanel} style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer' }}>&times;</button>
                            </div>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                            {/* Workflow Actions */}
                            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#eef2f7', borderRadius: '8px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#0056b3' }}>상태 관리</label>
                                {isAdmin ? (
                                    // Admin: Can force change status (rollback inclusive)
                                    <select
                                        value={selectedQuote.status}
                                        onChange={(e) => handleStatusChange(e.target.value)}
                                        style={{ width: '100%', padding: '10px', borderRadius: '4px' }}
                                    >
                                        {statusOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                    </select>
                                ) : (
                                    // User: Only Next Step
                                    nextStatus ? (
                                        <button
                                            onClick={() => handleStatusChange(nextStatus)}
                                            style={{ width: '100%', padding: '10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                                        >
                                            다음 단계로 진행 ({nextStatus} →)
                                        </button>
                                    ) : (
                                        <div style={{ color: '#28a745', fontWeight: 'bold', textAlign: 'center' }}>🎉 최종 단계 (현재 상태: {selectedQuote.status})</div>
                                    )
                                )}
                                <button
                                    onClick={() => setShowGenerator(true)}
                                    style={{ width: '100%', marginTop: '10px', padding: '10px', backgroundColor: '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                                >
                                    📄 견적서 생성 / 관리
                                </button>
                            </div>

                            {/* Sales Rep Assignment */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>영업 담당자 {isAdmin ? '(관리자 지정)' : '(보기 전용)'}</label>
                                <select
                                    value={selectedQuote.sales_rep_id || ''}
                                    onChange={(e) => handleAssignRep(e.target.value)}
                                    disabled={!isAdmin}
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ced4da', backgroundColor: isAdmin ? 'white' : '#f0f0f0' }}
                                >
                                    <option value="">미지정</option>
                                    {users.map(u => (
                                        <option key={u.id} value={u.id}>{u.username}</option>
                                    ))}
                                </select>
                            </div>

                            <hr style={{ borderTop: '1px solid #eee', margin: '20px 0' }} />

                            {/* Info Fields */}
                            <InfoRow label="업체명" value={selectedQuote.company_name} />
                            <InfoRow label="고객 담당자" value={selectedQuote.contact_name} />
                            <InfoRow label="연락처" value={selectedQuote.phone} />
                            <InfoRow label="이메일" value={selectedQuote.email} />
                            <InfoRow label="SKU / 물량" value={`${selectedQuote.sku} / ${selectedQuote.daily_volume}`} />
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>필요 모듈</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                    {selectedQuote.modules?.map(m => (
                                        <span key={m} style={{ padding: '2px 6px', backgroundColor: '#f1f3f5', borderRadius: '4px', fontSize: '12px' }}>{m}</span>
                                    ))}
                                </div>
                            </div>
                            <InfoRow label="유입 경로" value={selectedQuote.referral_sources.join(', ') + (selectedQuote.referral_source_etc ? ` (${selectedQuote.referral_source_etc})` : '')} />

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>고객 메모 (요청사항)</label>
                                <div style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                                    {selectedQuote.message}
                                </div>
                            </div>

                            <hr style={{ borderTop: '1px solid #eee', margin: '20px 0' }} />

                            {/* Internal Remarks / Logs */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>내부 업무 히스토리 / 비고</label>
                                <div style={{
                                    height: '150px', overflowY: 'auto', padding: '10px', backgroundColor: '#fff',
                                    border: '1px solid #ced4da', borderRadius: '4px', marginBottom: '10px', fontSize: '12px', whiteSpace: 'pre-wrap'
                                }}>
                                    {selectedQuote.internal_remarks || '(기록 없음)'}
                                </div>
                                <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                                    <input
                                        type="text"
                                        value={panelRemarks}
                                        onChange={(e) => setPanelRemarks(e.target.value)}
                                        placeholder="새로운 기록 추가..."
                                        style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ced4da' }}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSaveRemarks()}
                                    />
                                    <button onClick={handleSaveRemarks} style={{ padding: '8px 12px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>저장</button>
                                </div>

                                <div style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>
                                    * 견적 생성, 상태 변경, 수동 메모 등 주요 이력이 이곳에 자동 기록됩니다.
                                </div>
                            </div>

                            <div style={{ fontSize: '11px', color: '#999', textAlign: 'right', marginTop: '10px' }}>
                                최초 접수: {new Date(selectedQuote.created_at).toLocaleString()}<br />
                                마지막 수정: {selectedQuote.status_updated_at ? new Date(selectedQuote.status_updated_at).toLocaleString() : '-'}
                            </div>

                        </div>
                    </>
                )}
            </div>

            {/* Manual Entry Modal */}
            {showManualModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100
                }}>
                    <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', width: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <h3>{isAdmin ? '관리자 수동 견적 입력' : '신규 견적 등록 (영업담당)'}</h3>
                        <form onSubmit={handleManualSubmit}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <label>업체명
                                    <input
                                        type="text"
                                        value={manualForm.companyName}
                                        onChange={e => setManualForm({ ...manualForm, companyName: e.target.value })}
                                        onBlur={(e) => checkDuplicate(e.target.value)}
                                        required
                                        style={{ width: '100%', padding: '5px', borderColor: '#ced4da' }}
                                        placeholder="입력 후 포커스 이동 시 중복 확인"
                                    />
                                </label>
                                <label>담당자 <input type="text" value={manualForm.contactName} onChange={e => setManualForm({ ...manualForm, contactName: e.target.value })} required style={{ width: '100%', padding: '5px' }} /></label>
                                <label>연락처 <input type="text" value={manualForm.phone} onChange={e => setManualForm({ ...manualForm, phone: e.target.value })} required style={{ width: '100%', padding: '5px' }} /></label>
                                <label>이메일 <input type="email" value={manualForm.email} onChange={e => setManualForm({ ...manualForm, email: e.target.value })} required style={{ width: '100%', padding: '5px' }} /></label>
                                <label>SKU <input type="text" value={manualForm.sku} onChange={e => setManualForm({ ...manualForm, sku: e.target.value })} style={{ width: '100%', padding: '5px' }} /></label>
                                <label>일출고량 <input type="text" value={manualForm.dailyVolume} onChange={e => setManualForm({ ...manualForm, dailyVolume: e.target.value })} style={{ width: '100%', padding: '5px' }} /></label>
                            </div>
                            <label style={{ display: 'block', marginTop: '10px' }}>메모</label>
                            <textarea value={manualForm.message} onChange={e => setManualForm({ ...manualForm, message: e.target.value })} style={{ width: '100%', minHeight: '80px', padding: '5px' }} />

                            <label style={{ display: 'block', marginTop: '10px' }}>초기 상태</label>
                            <select value={manualForm.status} onChange={e => setManualForm({ ...manualForm, status: e.target.value })} style={{ width: '100%', padding: '5px' }}>
                                <option value="견적요청">견적요청</option>
                                <option value="1차상담 완료">1차상담 완료</option>
                                <option value="1차견적서 완료">1차견적서 완료</option>
                            </select>

                            {isAdmin && (
                                <>
                                    <label style={{ display: 'block', marginTop: '10px' }}>담당자 지정</label>
                                    <select
                                        value={manualForm.salesRepId || ''}
                                        onChange={(e) => setManualForm({ ...manualForm, salesRepId: e.target.value })}
                                        style={{ width: '100%', padding: '5px' }}
                                    >
                                        <option value="">미지정</option>
                                        {users.map(u => (
                                            <option key={u.id} value={u.id}>{u.username}</option>
                                        ))}
                                    </select>
                                </>
                            )}

                            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button type="button" onClick={() => setShowManualModal(false)} style={{ padding: '8px 16px', cursor: 'pointer' }}>취소</button>
                                <button type="submit" style={{ padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>저장</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Quote Generator Modal */}
            {showGenerator && selectedQuote && (
                <QuoteGenerator
                    request={selectedQuote}
                    onClose={() => setShowGenerator(false)}
                    onSaveSuccess={() => { fetchQuotes(); }}
                />
            )}

            {/* History Modal */}
            {showHistoryModal && selectedQuote && (
                <HistoryModal
                    requestId={selectedQuote.id}
                    onClose={() => setShowHistoryModal(false)}
                />
            )}

            {/* Simple Overlay for panel */}
            {isPanelOpen && (
                <div onClick={closePanel} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 999 }} />
            )}
        </div>
    );
};

const InfoRow = ({ label, value }: { label: string, value: string }) => (
    <div style={{ marginBottom: '10px' }}>
        <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '2px' }}>{label}</label>
        <div style={{ fontSize: '14px' }}>{value || '-'}</div>
    </div>
);

export default QuoteList;
